// Design learning and feedback system
const FEEDBACK_STORAGE_KEY = 'lamp_design_feedback';

// Load feedback data from localStorage
const loadFeedbackData = () => {
  const stored = localStorage.getItem(FEEDBACK_STORAGE_KEY);
  if (stored) {
    try {
      const data = JSON.parse(stored);
      // Ensure all required arrays exist
      if (!data.positiveExamples) data.positiveExamples = [];
      if (!data.negativeExamples) data.negativeExamples = [];
      if (!data.extremeCombinations) data.extremeCombinations = [];
      if (!data.parameterWeights) {
        data.parameterWeights = {
          diameter: { min: 115, max: 220, weight: 1 },
          height: { min: 150, max: 240, weight: 1 },
          profilePoints: { weight: 1 },
          modifiers: {
            displacement: { weight: 1 },
            twist: { weight: 1 },
            radial_wave: { weight: 1 },
            radial_wrinkle: { weight: 1 }
          }
        };
      }
      return data;
    } catch (error) {
      console.error('Error parsing stored feedback data:', error);
    }
  }
  
  // Return default structure if no stored data or error occurred
  return {
    positiveExamples: [],
    negativeExamples: [],
    extremeCombinations: [],
    parameterWeights: {
      diameter: { min: 115, max: 220, weight: 1 },
      height: { min: 150, max: 240, weight: 1 },
      profilePoints: { weight: 1 },
      modifiers: {
        displacement: { weight: 1 },
        twist: { weight: 1 },
        radial_wave: { weight: 1 },
        radial_wrinkle: { weight: 1 }
      }
    }
  };
};

// Save feedback data to localStorage
const saveFeedbackData = (data) => {
  console.log('Saving feedback data:', data);
  try {
    localStorage.setItem(FEEDBACK_STORAGE_KEY, JSON.stringify(data));
    console.log('Feedback data saved successfully');
    // Verify the data was saved
    const savedData = localStorage.getItem(FEEDBACK_STORAGE_KEY);
    console.log('Verified saved data:', savedData);
  } catch (error) {
    console.error('Error saving feedback data:', error);
  }
};

// Check if a modifier combination is extreme
const isExtremeCombination = (modifiers) => {
  const extremeThresholds = {
    displacement: { amplitude: 0.8, frequency: 0.8 },
    twist: { angle: 180 },
    radial_wave: { amplitude: 0.8, frequency: 0.8 },
    radial_wrinkle: { amplitude: 0.8, frequency: 0.8 },
    facet: { facetCount: 32, facetDepth: 0.9 }
  };

  return modifiers.some(modifier => {
    if (modifier.disabled) return false;
    const thresholds = extremeThresholds[modifier.type];
    if (!thresholds) return false;

    return Object.entries(thresholds).some(([param, threshold]) => {
      const value = Math.abs(modifier.parameters[param] || 0);
      return value >= threshold;
    });
  });
};

// Add a design to the feedback data
export const addFeedback = (design, isPositive) => {
  console.log('Adding feedback for design:', design);
  console.log('Is positive feedback:', isPositive);
  
  const feedbackData = loadFeedbackData();
  console.log('Loaded feedback data:', feedbackData);
  
  // Store the design in the appropriate list
  if (isPositive) {
    feedbackData.positiveExamples.push(design);
    console.log('Added to positive examples');
    
    // Check if this is an extreme combination that worked
    if (isExtremeCombination(design.modifiers)) {
      feedbackData.extremeCombinations.push({
        modifiers: design.modifiers,
        baseShape: design.baseShape
      });
      console.log('Added to extreme combinations');
    }
  } else {
    feedbackData.negativeExamples.push(design);
    console.log('Added to negative examples');
  }
  
  // Update parameter weights based on feedback
  updateParameterWeights(feedbackData);
  
  // Save updated feedback data
  saveFeedbackData(feedbackData);
};

// Get successful extreme combinations
export const getExtremeCombinations = () => {
  const feedbackData = loadFeedbackData();
  return feedbackData.extremeCombinations;
};

// Update parameter weights based on feedback data
const updateParameterWeights = (feedbackData) => {
  const weights = feedbackData.parameterWeights;
  
  // Analyze positive examples to adjust weights
  feedbackData.positiveExamples.forEach(design => {
    // Adjust diameter weight
    const diameterRange = weights.diameter;
    if (design.baseShape.diameter >= diameterRange.min && 
        design.baseShape.diameter <= diameterRange.max) {
      diameterRange.weight *= 1.1; // Increase weight for successful range
    }
    
    // Adjust height weight
    const heightRange = weights.height;
    if (design.baseShape.height >= heightRange.min && 
        design.baseShape.height <= heightRange.max) {
      heightRange.weight *= 1.1;
    }
    
    // Adjust modifier weights
    design.modifiers.forEach(modifier => {
      if (!modifier.disabled && weights.modifiers[modifier.type]) {
        weights.modifiers[modifier.type].weight *= 1.1;
      }
    });
  });
  
  // Analyze negative examples to adjust weights
  feedbackData.negativeExamples.forEach(design => {
    // Adjust diameter weight
    const diameterRange = weights.diameter;
    if (design.baseShape.diameter >= diameterRange.min && 
        design.baseShape.diameter <= diameterRange.max) {
      diameterRange.weight *= 0.9; // Decrease weight for unsuccessful range
    }
    
    // Adjust height weight
    const heightRange = weights.height;
    if (design.baseShape.height >= heightRange.min && 
        design.baseShape.height <= heightRange.max) {
      heightRange.weight *= 0.9;
    }
    
    // Adjust modifier weights
    design.modifiers.forEach(modifier => {
      if (!modifier.disabled && weights.modifiers[modifier.type]) {
        weights.modifiers[modifier.type].weight *= 0.9;
      }
    });
  });
  
  // Normalize weights to prevent extreme values
  normalizeWeights(weights);
};

// Normalize weights to keep them in a reasonable range
const normalizeWeights = (weights) => {
  const normalizeRange = (range) => {
    range.weight = Math.max(0.1, Math.min(2.0, range.weight));
  };
  
  normalizeRange(weights.diameter);
  normalizeRange(weights.height);
  
  Object.values(weights.modifiers).forEach(modifier => {
    normalizeRange(modifier);
  });
};

// Get weighted random value within a range
const getWeightedRandom = (min, max, weight) => {
  const range = max - min;
  const weightedRange = range * weight;
  const offset = (range - weightedRange) / 2;
  return min + offset + (Math.random() * weightedRange);
};

// Generate a design using learned preferences
export const generateLearnedDesign = () => {
  const feedbackData = loadFeedbackData();
  const weights = feedbackData.parameterWeights;
  
  // 30% chance to try an extreme combination
  if (Math.random() < 0.3 && feedbackData.extremeCombinations.length > 0) {
    const randomExtreme = feedbackData.extremeCombinations[
      Math.floor(Math.random() * feedbackData.extremeCombinations.length)
    ];
    
    // Use the extreme combination's modifiers but with slightly randomized parameters
    const modifiers = randomExtreme.modifiers.map(modifier => ({
      ...modifier,
      parameters: Object.fromEntries(
        Object.entries(modifier.parameters).map(([key, value]) => [
          key,
          value * (0.9 + Math.random() * 0.2) // Randomize by ±10%
        ])
      )
    }));
    
    return {
      baseShape: randomExtreme.baseShape,
      modifiers
    };
  }
  
  // Otherwise generate normally using weights
  const diameter = getWeightedRandom(
    weights.diameter.min,
    weights.diameter.max,
    weights.diameter.weight
  );
  
  const height = getWeightedRandom(
    weights.height.min,
    weights.height.max,
    weights.height.weight
  );
  
  const profilePoints = [
    { x: 0.9, y: 0.0 },
    { x: 0.9 + (Math.random() * 0.2), y: 0.5 },
    { x: 0.9 + (Math.random() * 0.2), y: 0.9 }
  ];
  
  const modifiers = Object.entries(weights.modifiers)
    .filter(([_, config]) => Math.random() < config.weight)
    .map(([type, _]) => ({
      type,
      disabled: false,
      parameters: {} // Parameters would be generated based on type
    }));
  
  return {
    baseShape: {
      diameter,
      height,
      profilePoints
    },
    modifiers
  };
};

// Clear all feedback data
export const clearFeedbackData = () => {
  localStorage.removeItem(FEEDBACK_STORAGE_KEY);
}; 